import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideUserUpdate: React.FC = () => {
  return (
    <GuidePageLayout
      title="ユーザー情報を変更する"
      description="既存ユーザーのメールアドレス・ユーザー名・パスワードを変更します。"
      breadcrumbs={[{ label: 'ユーザー管理', path: '/guide/user/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者'],
            ['ログイン状態', 'ログイン済みであること'],
            ['対象ユーザー', '存在し、自分自身でないこと'],
          ]}
        />
      </GuideSection>

      <GuideSection title="画面モード">
        <GuideTable
          headers={['モード', 'タイトル', '説明']}
          rows={[
            [
              '参照モード',
              'ユーザー詳細',
              'ユーザー情報を参照専用で表示。編集ボタンで編集モードに切替',
            ],
            [
              '編集モード',
              'ユーザー詳細（編集中）',
              '各フィールドが入力可能になる。戻る矢印で参照モードに戻る',
            ],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: 'ユーザー管理画面を開く' },
            {
              label: 'ユーザー一覧から対象ユーザーを選択する',
              description: '必要に応じて検索条件を入力して絞り込みます。',
            },
            {
              label: '参照モード（ユーザー詳細）が表示される',
              description:
                'ヘッダー右上にユーザーIDが表示されます。コピーボタンでクリップボードにコピーできます。',
              content: (
                <GuideMockContainer label="参照モード（ヘッダー部分）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        ユーザー詳細
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          user01
                        </Typography>
                        <IconButton size="small">
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「編集」ボタンを押下して編集モードに切り替える',
              content: (
                <GuideMockContainer label="参照モード フッター">
                  <GuideMockButton label="編集" startIcon={<EditIcon />} />
                </GuideMockContainer>
              ),
            },
            {
              label: '編集フォームで変更したい項目を修正する',
              description:
                '変更しない項目はそのままにします。パスワードは変更する場合のみ入力します。',
              content: (
                <GuideMockContainer label="編集モード">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        ユーザー詳細（編集中）
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <GuideMockTextField label="ユーザー名" value="サンプル太郎" />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField label="メールアドレス" value="sample@example.com" />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField
                          label="パスワード（変更する場合のみ入力）"
                          type="password"
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「更新」ボタンを押下する',
              content: (
                <GuideMockContainer label="編集モード フッター">
                  <GuideMockButton label="更新" startIcon={<SaveIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', 'モード', '動作']}
          rows={[
            ['← 戻る矢印（参照モード）', '参照', '詳細画面を閉じて一覧に戻る'],
            ['IDコピーボタン', '共通', 'ユーザーIDをクリップボードにコピー'],
            ['編集ボタン', '参照', '編集モードに切り替える（自分自身の場合は非表示）'],
            ['← 戻る矢印（編集モード）', '編集', '編集内容を破棄して参照モードに戻る'],
            ['削除ボタン', '編集', 'ユーザーを削除する'],
            ['更新ボタン', '編集', '入力内容でユーザー情報を更新する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox type="warning">ユーザーIDは変更できません。</GuideInfoBox>
          <GuideInfoBox type="warning">
            自分自身のアカウントは参照のみ可能で、編集ボタンは表示されません。
          </GuideInfoBox>
          <GuideInfoBox>
            編集モードで戻る矢印をクリックすると、未保存の変更は破棄されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideUserUpdate;
